import React, { useState, useCallback } from "react";
import { useAccountToDisplaySelector } from "../../../redux/actions/user";
import { useDateRangeSelector, setLoading } from "../../../redux/actions/app";
import { useDispatch } from "react-redux";
import Axios from "axios";
import CONFIG from "../../../model/TSConfig";
import { tt } from "../../../utils/i18n/i18n";
import moment from "moment-timezone";
import { saveAs } from "file-saver";
import { PaginatedTable } from "./PaginatedTable";

const PAGE_SIZE = 20;

interface OptinUser {
  userEmail: string;
  userName: string;
  optin: boolean;
  birthday: string;
  userPhoneNumber: string;
}

export const OptinsTable = () => {
  const dispatch = useDispatch();
  const { accountToDisplay } = useAccountToDisplaySelector();
  const dateRange = useDateRangeSelector();
  const [totalCount, setTotalCount] = useState(0);
  const [users, setUsers] = useState<OptinUser[]>([]);

  const fetchCount = useCallback(async () => {
    if (!accountToDisplay?.trackingID || !dateRange) {
      return;
    }

    try {
      const response = await Axios({
        method: "get",
        url: `${CONFIG.jetpackBackendUrl}/custdash/optins/count`,
        params: {
          owner: accountToDisplay?.trackingID,
          sts: moment(dateRange.start).unix(),
          ets: moment(dateRange.end).unix()
        }
      });

      if (response.data?.payload?.[0].count) {
        setTotalCount(response.data.payload[0].count);
      }
    } catch (error) {
      console.error("Error fetching optins count:", error);
      setTotalCount(0);
    }
  }, [accountToDisplay?.trackingID, dateRange]);

  const fetchOptins = useCallback(
    async (pageIndex: number) => {
      if (!accountToDisplay?.trackingID || !dateRange) {
        return;
      }

      dispatch(setLoading(true));

      try {
        const response = await Axios({
          method: "get",
          url: `${CONFIG.jetpackBackendUrl}/custdash/optins`,
          params: {
            owner: accountToDisplay?.trackingID,
            sts: moment(dateRange.start).unix(),
            ets: moment(dateRange.end).unix(),
            pageIndex,
            pageSize: PAGE_SIZE
          }
        });

        if (response.data?.payload?.optins) {
          setUsers(response.data.payload.optins);
        }
      } catch (error) {
        console.error("Error fetching optins:", error);
        setUsers([]);
      } finally {
        dispatch(setLoading(false));
      }
    },
    [accountToDisplay, dateRange, dispatch]
  );

  const handleDownload = useCallback(async () => {
    if (!accountToDisplay?.trackingID || !dateRange) {
      return;
    }

    dispatch(setLoading(true));

    try {
      const response = await Axios({
        method: "get",
        url: `${CONFIG.jetpackBackendUrl}/custdash/optins/csv`,
        params: {
          owner: accountToDisplay?.trackingID,
          sts: moment(dateRange.start).unix(),
          ets: moment(dateRange.end).unix()
        },
        responseType: "blob"
      });

      const startDate = moment(dateRange.start).format("YYYY-MM-DD");
      const endDate = moment(dateRange.end).format("YYYY-MM-DD");
      const fileName = `optins_${accountToDisplay?.placeName}_${startDate}_${endDate}.csv`;

      saveAs(response.data, fileName);
    } catch (error) {
      console.error("Error downloading CSV:", error);
    } finally {
      dispatch(setLoading(false));
    }
  }, [accountToDisplay, dateRange, dispatch]);

  const renderTableHeaders = useCallback(() => {
    return (
      <tr>
        <th>{tt("email")}</th>
        <th>{tt("name")}</th>
        <th>{tt("birthday")}</th>
        <th>{tt("phone_number")}</th>
      </tr>
    );
  }, []);

  const renderTableRow = useCallback((user: OptinUser, index: number) => {
    return (
      <tr key={`optin-user-${index}`}>
        <td>{user.userEmail || "-"}</td>
        <td>{user.userName || "-"}</td>
        <td>
          {user.birthday
            ? moment(user.birthday).format(tt("date_format_full_date"))
            : "-"}
        </td>
        <td>{user.userPhoneNumber || "-"}</td>
      </tr>
    );
  }, []);

  if (!accountToDisplay?.trackingID) {
    return null;
  }

  return (
    <div className="optins-container">
      <PaginatedTable<OptinUser>
        pageSize={PAGE_SIZE}
        totalCount={totalCount}
        data={users}
        onFetchCount={fetchCount}
        onFetchData={fetchOptins}
        onDownload={handleDownload}
        renderHeaders={renderTableHeaders}
        renderRow={renderTableRow}
        keyPrefix="optins"
        showDownloadButton={true}
        trackingID={accountToDisplay.trackingID}
      />
    </div>
  );
};
